/**
 * API 클라이언트 (DESIGN §7.1 · §8.7).
 *
 * TanStack Query 키 = [endpoint, 직렬화된 FilterState]. staleTime 5분 · ETag 활용.
 * 서버가 이미 `Cache-Control: max-age=300` 과 ETag 를 주므로 브라우저 캐시가
 * 재검증을 처리한다 — 프론트는 키만 정확히 맞추면 된다.
 */

import type { Envelope, Mode } from './types'

export const API_BASE = '/api/v1'

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly hint: string = '',
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

export interface QueryFilters {
  mode: Mode
  from?: string | null
  to?: string | null
  busi?: string[]
  zcode?: string[]
  zscode?: string[]
  kind?: string[]
  chgerType?: string[]
  maker?: string[]
  /** 특정 연식 (복수). 기간과 AND (§8.10.2) */
  year?: number[]
  yearFrom?: number | null
  yearTo?: number | null
  floor?: 'F' | 'B' | null
  minCoverage?: number
}

/** 필터 → 쿼리스트링. 빈 배열·null 은 보내지 않는다 (서버 규약과 일치). */
export function toSearchParams(f: QueryFilters, extra: Record<string, string | number> = {}) {
  const p = new URLSearchParams()
  p.set('mode', f.mode)
  if (f.from) p.set('from', f.from)
  if (f.to) p.set('to', f.to)
  for (const [key, values] of [
    ['busi', f.busi],
    ['zcode', f.zcode],
    ['zscode', f.zscode],
    ['kind', f.kind],
    ['chger_type', f.chgerType],
    ['maker', f.maker],
  ] as const) {
    for (const v of values ?? []) p.append(key, v)
  }
  for (const y of f.year ?? []) p.append('year', String(y))
  if (f.yearFrom != null) p.set('year_from', String(f.yearFrom))
  if (f.yearTo != null) p.set('year_to', String(f.yearTo))
  if (f.floor) p.set('floor', f.floor)
  if (f.minCoverage != null) p.set('min_coverage', String(f.minCoverage))
  for (const [k, v] of Object.entries(extra)) p.set(k, String(v))
  return p
}

export async function fetchEnvelope<T>(
  path: string,
  filters: QueryFilters,
  extra: Record<string, string | number> = {},
  signal?: AbortSignal,
): Promise<Envelope<T>> {
  const url = `${API_BASE}${path}?${toSearchParams(filters, extra).toString()}`
  const res = await fetch(url, { signal, headers: { Accept: 'application/json' } })

  if (!res.ok) {
    let code = `http_${res.status}`
    let message = `요청이 실패했습니다 (HTTP ${res.status})`
    let hint = ''
    try {
      const body = (await res.json()) as { error?: { code: string; message_ko: string; hint: string } }
      if (body.error) {
        code = body.error.code
        message = body.error.message_ko
        hint = body.error.hint
      }
    } catch {
      /* 본문이 JSON 이 아니면 기본 메시지를 쓴다 */
    }
    throw new ApiRequestError(res.status, code, message, hint)
  }

  const body = (await res.json()) as Envelope<T>
  // §7.1 계약을 프론트에서도 확인한다. 서버가 어기면 조용히 그리지 않고 드러낸다.
  if (body?.meta == null || typeof body.meta.coverage !== 'number') {
    throw new ApiRequestError(
      200,
      'contract_violation',
      '응답에 meta.coverage 가 없습니다 (API 계약 위반).',
      '백엔드 버전을 확인하세요 — 커버리지 없는 수치는 표시하지 않습니다.',
    )
  }
  return body
}

/** 필터와 무관한 메타 조회 (manifest·health). */
export async function fetchPlain<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { signal, headers: { Accept: 'application/json' } })
  if (!res.ok) {
    throw new ApiRequestError(res.status, `http_${res.status}`, `요청이 실패했습니다 (HTTP ${res.status})`)
  }
  return (await res.json()) as T
}
